import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const VINTED_CONDITIONS = ["New with tags", "New without tags", "Very good", "Good", "Satisfactory"];
export const PACKAGES = [["small", "Small"], ["medium", "Medium"], ["large", "Large"]];
const FROM = { weight: "from weight", batch: "batch size", default: "your default", item: "this item" };
const label = "text-[10px] font-mono uppercase tracking-wider text-slate-500";
const box = "mt-1 h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800";

export function PackageChips({ value, onChange, auto, testid }) {
  const chip = (k, text) => (
    <button key={k || "auto"} type="button" onClick={() => onChange(k)} aria-pressed={value === k} data-testid={`${testid}-${k || "auto"}`}
      className={`h-8 flex-1 rounded-full border px-2 text-[11px] font-semibold transition-colors ${value === k ? "border-[#09B1BA] bg-[#09B1BA] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#09B1BA]"}`}>{text}</button>
  );
  return (
    <>
      <div className="mt-1 flex gap-1.5" data-testid={testid}>{auto !== undefined && chip("", "Auto")}{PACKAGES.map(([k, t]) => chip(k, t))}</div>
      {auto && <p className="mt-1 text-[10px] text-slate-500" data-testid={`${testid}-auto-hint`}>Auto = {auto}</p>}
    </>
  );
}

// Everything the extension types into Vinted's upload form; blank boxes use what SnapList worked out (shown as the placeholder).
export default function VintedFields({ draft, value, onChange }) {
  const [auto, setAuto] = useState(null);
  const v = value || {};
  const [text, setText] = useState({ color: v.color || "", size: v.size || "" });
  useEffect(() => { setText({ color: v.color || "", size: v.size || "" }); }, [draft.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`/paste/vinted/${draft.id}`).then(({ data }) => setAuto(data.auto)).catch(() => setAuto({})); }, [draft.id, draft.condition, draft.package?.weight_lb, draft.package?.weight_oz]);
  const set = (k, val) => onChange({ ...v, [k]: val });
  const spec = (rx) => (draft.item_specifics || []).find((s) => rx.test(s.key || ""))?.value;
  const brand = spec(/^brand$/i);
  const autoColor = spec(/^(main )?colou?r$/i) || auto?.color;
  const autoSize = spec(/^(us shoe )?size( \((men|women)'s\))?$/i) || auto?.size;
  const pkgAuto = auto?.package ? `${PACKAGES.find((p) => p[0] === auto.package)?.[1]} (${FROM[auto.package_from] || ""})` : "";
  return (
    <div className="rounded-lg border border-[#09B1BA]/40 bg-[#09B1BA]/5 p-3" data-testid="vinted-fields">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#067A80]">Vinted upload</p>
      <p className="mt-1 text-[11px] text-slate-600" data-testid="vinted-summary">
        Category <b>{(draft.category || "").split(">").pop().trim() || "not set"}</b> · Brand <b>{brand || "not set"}</b> · Price <b>{draft.price ? `$${draft.price}` : "not set"}</b>
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="col-span-2"><span className={label}>Condition</span>
          <select value={v.condition || ""} onChange={(e) => set("condition", e.target.value)} className={box} data-testid="vinted-condition">
            <option value="">{auto?.condition ? `Auto · ${auto.condition}` : "Auto"}</option>
            {VINTED_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label><span className={label}>Color</span>
          <input value={text.color} maxLength={40} placeholder={autoColor || "e.g. Blue"} onChange={(e) => setText((t) => ({ ...t, color: e.target.value }))}
            onBlur={() => text.color !== (v.color || "") && set("color", text.color.trim())} className={box} data-testid="vinted-color" />
        </label>
        <label><span className={label}>Size</span>
          <input value={text.size} maxLength={40} placeholder={autoSize || "e.g. M"} onChange={(e) => setText((t) => ({ ...t, size: e.target.value }))}
            onBlur={() => text.size !== (v.size || "") && set("size", text.size.trim())} className={box} data-testid="vinted-size" />
        </label>
      </div>
      <p className={`${label} mt-2`}>Shipping size</p>
      <PackageChips value={v.package || ""} onChange={(k) => set("package", k)} auto={pkgAuto} testid="vinted-package" />
      <p className="mt-1.5 text-[10px] text-slate-500">The extension adds the photos, then fills title, description, category, brand, condition, color, size, price and shipping size on Vinted and presses Upload.</p>
    </div>
  );
}

// One shipping size for every item in a batch (items can still pick their own).
export function BatchVintedPackage({ batch, onSaved }) {
  const pick = async (k) => {
    const { data } = await api.put(`/photo-batches/${batch.id}/vinted-package`, { package: k || null });
    onSaved(data);
  };
  return (
    <div className="mt-2 text-[11px] font-semibold text-slate-600" data-testid="batch-vinted-package">
      Vinted shipping size for this batch
      <PackageChips value={batch.vinted_package || ""} onChange={pick} auto="" testid="batch-vinted-pkg" />
    </div>
  );
}
